import {Component, OnInit, inject} from '@angular/core';
import {
  ApplicationStatus,
  ButtonSize,
  ButtonState,
  ButtonType,
  IApplication, IApplicationSocketResponse,
  IJobOffer,
} from '../../../../../assets/types/dtoTypes';
import {Location, NgClass, NgIf} from '@angular/common';
import {ApplicationsOverviewPageService} from './applications-overview-page.service';
import {forkJoin} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {NotificationService} from '../../../../shared/services/notifications.service';
import {BIPPERZ_BREAKPOINTS, BreakpointsService} from '../../../../shared/services/breakpoint.service';
import {ExternalHeaderComponent} from "../../../../shared/external-header/external-header.component";
import {ButtonComponent} from "../../../../shared/button/button.component";
import {TranslateModule} from "@ngx-translate/core";
import {FooterComponent} from "../../../../shared/footer/footer.component";
import {ApplicationRow, CandidatesTableComponent} from "./candidates-table/candidates-table.component";
import {LoadMaskComponent} from "../../../../shared/load-mask/load-mask.component";

@Component({
  selector: 'app-applications-overview-page',
  templateUrl: './applications-overview-page.component.html',
  styleUrl: './applications-overview-page.component.scss',
  imports: [
    ExternalHeaderComponent,
    NgClass,
    ButtonComponent,
    TranslateModule,
    FooterComponent,
    CandidatesTableComponent,
    NgIf,
    LoadMaskComponent
  ],
  standalone: true
})
export class ApplicationsOverviewPageComponent implements OnInit {
  private breakpointsService = inject(BreakpointsService);
  private notificationService = inject(NotificationService);
  private route = inject(ActivatedRoute);
  private applicationOverviewService = inject(ApplicationsOverviewPageService);
  private location = inject(Location);


  isMobile = false;

  isLoading = false;

  protected readonly ButtonType = ButtonType;

  protected readonly ButtonState = ButtonState;

  protected readonly ButtonSize = ButtonSize;

  applications: IApplication[] = [];

  rows: ApplicationRow[] = [];

  jobOffers: IJobOffer[] = [];

  matches: IJobOffer[] = [];

  interviews: IJobOffer[] = [];

  interviewed: IJobOffer[] = [];

  offered: IJobOffer[] = [];

  rejected: IJobOffer[] = [];

  hired: IJobOffer[] = [];

  pending: IJobOffer[] = [];


  constructor() {

    this.notificationService.onApplicationUpdated().subscribe((socketUpdate: IApplicationSocketResponse) => {
      if (!socketUpdate) return;

      const app = this.applications.find(application => application.id === socketUpdate.applicationId);
      if (!app) return;

      app.status = socketUpdate.newStatus.toUpperCase() as ApplicationStatus;

      this.recomputeApplicationCategories();
    });

    this.breakpointsService.observe([BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down]).subscribe((result) => {
      this.isMobile = result.s || result.m_down;
    });

  }

  private buildRows(): void {
    const byId = new Map(this.jobOffers.map(o => [o.id, o]));
    this.rows = this.applications
      .map(app => {
        const offer = byId.get(app.jobOfferId);
        return offer ? {app, offer} as ApplicationRow : null;
      })
      .filter((x): x is ApplicationRow => !!x);
    this.isLoading = false;
  }

  private recomputeApplicationCategories() {
    const mapStatus = (status: string) => status.toUpperCase();
    const getIdsByStatus = (status: string) =>
      this.applications.filter(a => mapStatus(a.status) === status).map(a => a.jobOfferId);

    this.pending = this.jobOffers.filter(offer => getIdsByStatus(ApplicationStatus.PENDING).includes(offer.id));
    this.matches = this.jobOffers.filter(offer => getIdsByStatus(ApplicationStatus.SHORTLISTED).includes(offer.id));
    this.interviews = this.jobOffers.filter(offer => getIdsByStatus(ApplicationStatus.INVITED_TO_INTERVIEW).includes(offer.id));
    this.interviewed = this.jobOffers.filter(offer => getIdsByStatus(ApplicationStatus.INTERVIEWED).includes(offer.id));
    this.offered = this.jobOffers.filter(offer => getIdsByStatus(ApplicationStatus.OFFERED).includes(offer.id));
    this.hired = this.jobOffers.filter(offer => getIdsByStatus(ApplicationStatus.ACCEPTED).includes(offer.id));
    this.rejected = this.jobOffers.filter(offer => getIdsByStatus(ApplicationStatus.REJECTED).includes(offer.id));

    // keep rows in sync
    this.buildRows();
  }


  ngOnInit(): void {
    const userId = this.route.snapshot.paramMap.get('id');
    if (!userId) return;

    this.isLoading = true;
    this.applicationOverviewService.getAllApplicationsByUserId(userId).subscribe(applications => {
      this.applications = applications;

      if (applications.length > 0) {
        const jobOfferRequests = applications.map(app => this.applicationOverviewService.getJobOfferById(app.jobOfferId));
        forkJoin(jobOfferRequests).subscribe(jobOffers => {
          this.jobOffers = jobOffers;
          this.recomputeApplicationCategories();
          this.isLoading = false;
        });
      } else {
        this.isLoading = false;
      }
    });
    this.notificationService.onApplicationUpdated().subscribe((socketUpdate: IApplicationSocketResponse) => {
      if (!socketUpdate) return;
      const app = this.applications.find(a => a.id === socketUpdate.applicationId);
      if (!app) return;
      app.status = socketUpdate.newStatus.toUpperCase() as ApplicationStatus;
      this.recomputeApplicationCategories();
    });
  }

  accept($event: string) {
    const applicationToAccept = this.applications.find(app => app.jobOfferId === $event);
    if (!applicationToAccept) {
      return;
    }

    this.applicationOverviewService.accept(applicationToAccept.id).subscribe({
      next: () => {
        this.applications = this.applications.filter(app => app.jobOfferId !== applicationToAccept.id);
        this.jobOffers = this.jobOffers.filter(offer => offer.id !== applicationToAccept.id);
        this.recomputeApplicationCategories();
      },
      error: (err) => {
        console.error('Failed to accept application:', err);
      },
    });
  }

  goBack(): void {
    this.location.back();
  }

}
